/**
 * Analytics — Funnel API Contracts
 *
 * Wire format for FunnelController endpoints.
 * Reuses FUNNEL_STAGES from @autopia/event-schemas to keep the stage enum
 * in a single authoritative location.
 */

import { z } from 'zod';

// Mirrors FUNNEL_STAGES from @autopia/event-schemas — must stay in sync.
const FUNNEL_STAGES = [
  'page_view',
  'date_select',
  'add_to_cart',
  'checkout_start',
  'contact_entered',
  'payment_selected',
  'payment_initiated',
  'payment_completed',
] as const;

// ---------------------------------------------------------------------------
// Request schemas
// ---------------------------------------------------------------------------

export const TrackFunnelEventRequestSchema = z.object({
  stage: z.enum(FUNNEL_STAGES),
  sessionId: z.string().min(1).max(255),
  customerId: z.string().optional(),
  deviceType: z.string().max(50).optional(),
  referralSource: z.string().max(200).optional(),
  ticketType: z.string().max(100).optional(),
  paymentMethod: z.string().max(50).optional(),
  metadata: z.record(z.unknown()).optional(),
});
export type TrackFunnelEventRequest = z.infer<typeof TrackFunnelEventRequestSchema>;

const DATE_REGEX = /^\d{4}-\d{2}-\d{2}$/;

export const FunnelReportQuerySchema = z.object({
  from: z.string().regex(DATE_REGEX).optional(),
  to: z.string().regex(DATE_REGEX).optional(),
});
export type FunnelReportQuery = z.infer<typeof FunnelReportQuerySchema>;

export const ConversionRatesQuerySchema = z.object({
  from: z.string().regex(DATE_REGEX).optional(),
  to: z.string().regex(DATE_REGEX).optional(),
});
export type ConversionRatesQuery = z.infer<typeof ConversionRatesQuerySchema>;

// ---------------------------------------------------------------------------
// Response schemas
// ---------------------------------------------------------------------------

export const FunnelStageItemSchema = z.object({
  stage: z.string(),
  count: z.number().int(),
  conversionRate: z.number(),
});

export const FunnelReportSchema = z.object({
  period: z.object({
    from: z.string(),
    to: z.string(),
  }),
  stages: z.array(FunnelStageItemSchema),
  overallConversionRate: z.number(),
});
export type FunnelReport = z.infer<typeof FunnelReportSchema>;

export const FunnelStageRateSchema = z.object({
  stage: z.string(),
  count: z.number().int(),
  conversionRate: z.number().nullable(),
});

export const ConversionRatesResultSchema = z.object({
  from: z.string(),
  to: z.string(),
  stages: z.array(FunnelStageRateSchema),
  generatedAt: z.string().or(z.date()),
});
export type ConversionRatesResult = z.infer<typeof ConversionRatesResultSchema>;

export const DropOffTransitionSchema = z.object({
  fromStage: z.string(),
  toStage: z.string(),
  fromCount: z.number().int(),
  toCount: z.number().int(),
  dropOffCount: z.number().int(),
  dropOffPercent: z.number(),
});

export const DropOffAnalysisSchema = z.object({
  period: z.object({ from: z.string(), to: z.string() }),
  biggestDropOff: z.object({
    fromStage: z.string(),
    toStage: z.string(),
    dropOffCount: z.number().int(),
    dropOffPercent: z.number(),
  }),
  transitions: z.array(DropOffTransitionSchema),
});
export type DropOffAnalysis = z.infer<typeof DropOffAnalysisSchema>;
